import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Users, Plus, BarChart3, FileText, Settings, Share2, Copy, Crown, ChevronLeft, Clock } from 'lucide-react';
import Navbar from '../components/Navbar';
import Avatar from '../components/Avatar';
import { useAuth } from '../context/AuthContext';
import API from '../api/axios';
import toast from 'react-hot-toast';
import PostCard from '../components/PostCard';
import SurveyCard from '../components/SurveyCard';
import AdminMembers from './admin/AdminMembers';
import AdminOverview from './admin/AdminOverview';
import CreatePostModal from '../components/CreatePostModal';
import CreateSurveyModal from '../components/CreateSurveyModal';
import EditGroupModal from '../components/EditGroupModal';

const BASE_TABS = [
  { id: 'posts', label: 'البوستات', icon: <FileText size={16} /> },
  { id: 'surveys', label: 'الاستطلاعات', icon: <BarChart3 size={16} /> },
];
const MEMBER_TAB = { id: 'members', label: 'الأعضاء', icon: <Users size={16} /> };
const ORGANIZE_TAB = { id: 'organize', label: 'تنظيم', icon: <BarChart3 size={16} /> };

const GroupPage = () => {
  const { groupId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('posts');
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showCreateSurvey, setShowCreateSurvey] = useState(false);
  const [showEditGroup, setShowEditGroup] = useState(false);

  // user is considered admin if they appear in group.admins array (added by backend) or are owner
  const isAdmin = Boolean(
    group?.admins?.some(a => a._id === user?._id) ||
    group?.admin?._id === user?._id ||
    group?.admin === user?._id
  );

  useEffect(() => { loadGroup(); }, [groupId]);

  const loadGroup = async () => {
    try {
      const { data } = await API.get(`/groups/${groupId}`);
      setGroup(data.group);
    } catch (err) {
      toast.error('المجموعة غير موجودة');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyInvite = async () => {
    const link = `${window.location.origin}/join/${group.inviteToken}`;
    await navigator.clipboard.writeText(link);
    toast.success('تم نسخ رابط الدعوة! 🔗');
  };

  const handleRegenerateInvite = async () => {
    try {
      const { data } = await API.post(`/groups/${groupId}/regenerate-invite`);
      setGroup(prev => ({ ...prev, inviteToken: data.inviteToken }));
      toast.success('تم توليد رابط جديد ✅');
    } catch {
      toast.error('خطأ في توليد الرابط');
    }
  };

  if (loading) return (
    <div className="page-wrapper">
      <Navbar />
      <div className="loading-screen" style={{ minHeight: 'calc(100vh - 64px)' }}>
        <div className="spinner" />
        <p>جاري التحميل...</p>
      </div>
    </div>
  );

  return (
    <div className="page-wrapper">
      <Navbar />

      {/* Group Hero */}
      <div style={{
        background: group.image ? `url(${group.image}) center/cover` : 'var(--gradient-primary)',
        height: '200px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(10,15,30,0.9) 100%)'
        }} />
        <div className="container" style={{ height: '100%', display: 'flex', alignItems: 'flex-end', paddingBottom: '20px', position: 'relative', zIndex: 1 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              {isAdmin && <span className="badge badge-warning"><Crown size={12} /> أدمن</span>}
            </div>
            <h1 style={{ fontSize: '1.75rem', fontWeight: 800 }}>{group.name}</h1>
            <div style={{ display: 'flex', gap: '16px', color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', marginTop: '6px' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Users size={13} /> {group.members?.length || 0} / {group.maxMembers} عضو
              </span>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '8px' }}>
            {isAdmin && (
              <>
                <button className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}
                  onClick={handleCopyInvite}>
                  <Copy size={14} /> نسخ الرابط
                </button>
                <button className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}
                  onClick={() => setShowEditGroup(true)}>
                  <Settings size={14} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)', position: 'sticky', top: '64px', zIndex: 50 }}>
        <div className="container">
          <div style={{ display: 'flex', gap: '4px' }}>
            {[
              ...BASE_TABS,
              // members tab should be visible to anyone in the group (not just admins)
              MEMBER_TAB,
              isAdmin ? ORGANIZE_TAB : null
            ].filter(Boolean).map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '14px 18px',
                  background: 'none', border: 'none',
                  borderBottom: activeTab === tab.id ? '2px solid var(--primary)' : '2px solid transparent',
                  color: activeTab === tab.id ? 'var(--primary-light)' : 'var(--text-secondary)',
                  fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer',
                  transition: 'var(--transition)'
                }}
              >
                {tab.icon} {tab.label}
                {tab.id === 'posts' && group.posts?.length > 0 && (
                  <span className="badge badge-primary" style={{ padding: '1px 7px', fontSize: '0.7rem' }}>{group.posts.length}</span>
                )}
                {tab.id === 'surveys' && group.surveys?.length > 0 && (
                  <span className="badge badge-primary" style={{ padding: '1px 7px', fontSize: '0.7rem' }}>{group.surveys.length}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container" style={{ padding: '28px 20px' }}>

        {/* Admin Add Buttons */}
        {isAdmin && activeTab !== 'members' && (
          <div style={{ display: 'flex', gap: '10px', marginBottom: '24px' }}>
            {activeTab === 'posts' && (
              <button className="btn btn-primary btn-sm" onClick={() => setShowCreatePost(true)}>
                <Plus size={14} /> إضافة بوست
              </button>
            )}
            {activeTab === 'surveys' && (
              <button className="btn btn-primary btn-sm" onClick={() => setShowCreateSurvey(true)}>
                <Plus size={14} /> إضافة استطلاع
              </button>
            )}
          </div>
        )}

        {/* Posts Tab */}
        {activeTab === 'posts' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {!group.posts?.length ? (
              <div className="empty-state">
                <div className="empty-state-icon">📝</div>
                <h3>مفيش بوستات لحد دلوقتي</h3>
                <p>{isAdmin ? 'أضف أول بوست للمجموعة!' : 'انتظر الأدمن يضيف بوستات'}</p>
              </div>
            ) : (
              group.posts.map(post => (
                <PostCard key={post._id} post={post} isAdmin={isAdmin} groupId={groupId} onRefresh={loadGroup} currentUserId={user?._id} />
              ))
            )}
          </div>
        )}

        {/* Surveys Tab */}
        {activeTab === 'surveys' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {!group.surveys?.length ? (
              <div className="empty-state">
                <div className="empty-state-icon">📊</div>
                <h3>مفيش استطلاعات لحد دلوقتي</h3>
                <p>{isAdmin ? 'أضف استطلاع للمجموعة!' : 'انتظر الأدمن يضيف استطلاعات'}</p>
              </div>
            ) : (
              group.surveys.map(survey => (
                <SurveyCard key={survey._id} survey={survey} isAdmin={isAdmin} groupId={groupId} onRefresh={loadGroup} currentUserId={user?._id} />
              ))
            )}
          </div>
        )}

        {/* Members Tab */}
        {activeTab === 'members' && (
          <AdminMembers group={group} isAdmin={isAdmin} onRefresh={loadGroup} />
        )}

        {/* Organize Tab */}
        {activeTab === 'organize' && isAdmin && (
          <AdminOverview group={group} />
        )}
      </div>

      {/* Modals */}
      {showCreatePost && <CreatePostModal groupId={groupId} onClose={() => setShowCreatePost(false)} onSuccess={() => { setShowCreatePost(false); loadGroup(); }} />}
      {showCreateSurvey && <CreateSurveyModal groupId={groupId} onClose={() => setShowCreateSurvey(false)} onSuccess={() => { setShowCreateSurvey(false); loadGroup(); }} />}
      {showEditGroup && <EditGroupModal group={group} onClose={() => setShowEditGroup(false)} onSuccess={() => { setShowEditGroup(false); loadGroup(); }} />}
    </div>
  );
};

export default GroupPage;
